import { Zap, Plus, Edit, Trash2, Search, Building, User, Phone, Mail, Globe, MapPin, FileText, Calendar, Wrench, AlertCircle, CheckCircle, XCircle, Grid, List, Presentation, ChevronLeft, ChevronRight, Maximize2, X, Download } from 'lucide-react';
import { masterSupabase } from '../lib/supabase-master.js';
import { useState, useEffect } from 'react';
import MessdienstDashboard from "../components/messdienst/MessdienstDashboard";

const SuppliersPage = () => {
  // States
  const [activeTab, setActiveTab] = useState('suppliers');
  const [viewMode, setViewMode] = useState('table'); // 'table' oder 'cards'
  const [suppliers, setSuppliers] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [propertySuppliers, setPropertySuppliers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [owners, setOwners] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOwner, setSelectedOwner] = useState('');
  const [selectedProperty, setSelectedProperty] = useState('');
  const [notification, setNotification] = useState(null);

  // Modal states
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);
  const [showRoadmapModal, setShowRoadmapModal] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [editingItem, setEditingItem] = useState(null);

  // Form states
  const [supplierForm, setSupplierForm] = useState({
    name: '',
    type: '',
    contact_person: '',
    phone: '',
    email: '',
    website: '',
    address: '',
    notes: ''
  });

  const [contractForm, setContractForm] = useState({
    property_id: '',
    supplier_id: '',
    contract_number: '',
    start_date: '',
    end_date: '',
    monthly_cost: '',
    notes: ''
  });

  const [equipmentForm, setEquipmentForm] = useState({
    property_id: '',
    supplier_id: '',
    unit_id: '',
    type: '',
    brand: '',
    model: '',
    serial_number: '',
    installation_date: '',
    last_reading: '',
    last_maintenance_date: '',
    location: '',
    status: 'active',
    notes: ''
  });

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load owners
      const { data: ownersData, error: ownersError } = await masterSupabase
        .from('owners')
        .select('id, name')
        .order('name');

      if (ownersError) throw ownersError;

      // Load properties
      const { data: propertiesData, error: propertiesError } = await masterSupabase
        .from('properties')
        .select('id, property_name, owner_id')
        .order('property_name');

      if (propertiesError) throw propertiesError;

      // Load units
      const { data: unitsData, error: unitsError } = await masterSupabase
        .from('units')
        .select('id, unit_identifier, property_id')
        .order('unit_identifier');

      if (unitsError) throw unitsError;

      // Load suppliers
      const { data: suppliersData, error: suppliersError } = await masterSupabase
        .from('suppliers')
        .select('*')
        .order('name');

      if (suppliersError) throw suppliersError;

      // Load equipment
      const { data: equipmentData, error: equipmentError } = await masterSupabase
        .from('equipment')
        .select(`
          *,
          suppliers(name),
          properties(property_name, owner_id),
          units(unit_identifier)
        `)
        .order('created_at', { ascending: false });

      if (equipmentError) throw equipmentError;

      // Load property suppliers
      const { data: propertySupplierData, error: propertySupplierError } = await masterSupabase
        .from('property_suppliers')
        .select(`
          *,
          suppliers(name),
          properties(property_name, owner_id)
        `)
        .order('created_at', { ascending: false });

      if (propertySupplierError) throw propertySupplierError;

      setOwners(ownersData || []);
      setProperties(propertiesData || []);
      setUnits(unitsData || []);
      setSuppliers(suppliersData || []);
      setEquipment(equipmentData || []);
      setPropertySuppliers(propertySupplierData || []);

    } catch (error) {
      console.error('Error loading data:', error);
      showNotification('Fehler beim Laden der Daten', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Filter data based on selected owner and property
  const filteredSuppliers = suppliers.filter(supplier => {
    const matchesSearch = supplier.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         supplier.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         supplier.contact_person?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const filteredPropertySuppliers = propertySuppliers.filter(ps => {
    if (selectedOwner && ps.properties?.owner_id !== selectedOwner) return false;
    if (selectedProperty && ps.property_id !== selectedProperty) return false;
    
    const matchesSearch = ps.suppliers?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ps.contract_number?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const filteredEquipment = equipment.filter(eq => {
    if (selectedOwner && eq.properties?.owner_id !== selectedOwner) return false;
    if (selectedProperty && eq.property_id !== selectedProperty) return false;
    
    const matchesSearch = eq.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         eq.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         eq.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         eq.suppliers?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // CRUD operations for suppliers
  const handleSaveSupplier = async () => {
    try {
      if (editingItem) {
        const { error } = await masterSupabase
          .from('suppliers')
          .update(supplierForm)
          .eq('id', editingItem.id);
        
        if (error) throw error;
        showNotification('Versorger erfolgreich aktualisiert');
      } else {
        const { error } = await masterSupabase
          .from('suppliers')
          .insert([supplierForm]);
        
        if (error) throw error;
        showNotification('Versorger erfolgreich hinzugefügt');
      }

      setShowSupplierModal(false);
      setEditingItem(null);
      setSupplierForm({ name: '', type: '', contact_person: '', phone: '', email: '', website: '', address: '', notes: '' });
      loadData();
    } catch (error) {
      console.error('Error saving supplier:', error);
      showNotification('Fehler beim Speichern des Versorgers', 'error');
    }
  };

  const handleDeleteSupplier = async (id) => {
    if (!confirm('Sind Sie sicher, dass Sie diesen Versorger löschen möchten?')) return;

    try {
      const { error } = await masterSupabase
        .from('suppliers')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      showNotification('Versorger erfolgreich gelöscht');
      loadData();
    } catch (error) {
      console.error('Error deleting supplier:', error);
      showNotification('Fehler beim Löschen des Versorgers', 'error');
    }
  };

  // CRUD operations for contracts
  const handleSaveContract = async () => {
    try {
      if (editingItem) {
        const { error } = await masterSupabase
          .from('property_suppliers')
          .update(contractForm)
          .eq('id', editingItem.id);
        
        if (error) throw error;
        showNotification('Vertrag erfolgreich aktualisiert');
      } else {
        const { error } = await masterSupabase
          .from('property_suppliers')
          .insert([contractForm]);
        
        if (error) throw error;
        showNotification('Vertrag erfolgreich hinzugefügt');
      }

      setShowContractModal(false);
      setEditingItem(null);
      setContractForm({ property_id: '', supplier_id: '', contract_number: '', start_date: '', end_date: '', monthly_cost: '', notes: '' });
      loadData();
    } catch (error) {
      console.error('Error saving contract:', error);
      showNotification('Fehler beim Speichern des Vertrags', 'error');
    }
  };

  const handleDeleteContract = async (id) => {
    if (!confirm('Sind Sie sicher, dass Sie diesen Vertrag löschen möchten?')) return;

    try {
      const { error } = await masterSupabase
        .from('property_suppliers')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      showNotification('Vertrag erfolgreich gelöscht');
      loadData();
    } catch (error) {
      console.error('Error deleting contract:', error);
      showNotification('Fehler beim Löschen des Vertrags', 'error');
    }
  };

  // CRUD operations for equipment
  const handleSaveEquipment = async () => {
    try {
      if (editingItem) {
        const { error } = await masterSupabase
          .from('equipment')
          .update(equipmentForm)
          .eq('id', editingItem.id);
        
        if (error) throw error;
        showNotification('Gerätschaft erfolgreich aktualisiert');
      } else {
        const { error } = await masterSupabase
          .from('equipment')
          .insert([equipmentForm]);
        
        if (error) throw error;
        showNotification('Gerätschaft erfolgreich hinzugefügt');
      }

      setShowEquipmentModal(false);
      setEditingItem(null);
      setEquipmentForm({ property_id: '', supplier_id: '', unit_id: '', type: '', brand: '', model: '', serial_number: '', installation_date: '', last_reading: '', last_maintenance_date: '', location: '', status: 'active', notes: '' });
      loadData();
    } catch (error) {
      console.error('Error saving equipment:', error);
      showNotification('Fehler beim Speichern der Gerätschaft', 'error');
    }
  };

  const handleDeleteEquipment = async (id) => {
    if (!confirm('Sind Sie sicher, dass Sie diese Gerätschaft löschen möchten?')) return;

    try {
      const { error } = await masterSupabase
        .from('equipment')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      showNotification('Gerätschaft erfolgreich gelöscht');
      loadData();
    } catch (error) {
      console.error('Error deleting equipment:', error);
      showNotification('Fehler beim Löschen der Gerätschaft', 'error');
    }
  };

  // Render Suppliers in Card View
  const renderSuppliersCards = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {filteredSuppliers.map((supplier) => (
        <div key={supplier.id} className="bg-white dark:bg-slate-900 rounded-lg shadow p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg text-blue-600 dark:text-blue-400">
                <Zap className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{supplier.name}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">{supplier.type}</p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Kontakt</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">{supplier.contact_person}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Telefon</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">{supplier.phone}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">E-Mail</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">{supplier.email}</span>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => {
                setEditingItem(supplier);
                setSupplierForm(supplier);
                setShowSupplierModal(true);
              }}
              className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <Edit className="h-4 w-4 mr-1" />
              Bearbeiten
            </button>
            <button
              onClick={() => handleDeleteSupplier(supplier.id)}
              className="flex items-center px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Löschen
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  // Render Suppliers in Table View
  const renderSuppliersTable = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-slate-800">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Typ</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Kontakt</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Telefon</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">E-Mail</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Aktionen</th>
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-gray-700">
          {filteredSuppliers.map((supplier) => (
            <tr key={supplier.id}>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                {supplier.name}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                {supplier.type}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                {supplier.contact_person}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                {supplier.phone}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                {supplier.email}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setEditingItem(supplier);
                      setSupplierForm(supplier);
                      setShowSupplierModal(true);
                    }}
                    className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteSupplier(supplier.id)}
                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-black overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 h-screen overflow-y-auto">
        {/* Notification */}
        {notification && (
          <div className={`fixed top-4 right-4 z-50 p-4 rounded-md shadow-lg ${
            notification.type === 'error' 
              ? 'bg-red-500 text-white' 
              : 'bg-green-500 text-white'
          }`}>
            <div className="flex items-center">
              {notification.type === 'error' ? (
                <XCircle className="h-5 w-5 mr-2" />
              ) : (
                <CheckCircle className="h-5 w-5 mr-2" />
              )}
              {notification.message}
            </div>
          </div>
        )}

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
                <Zap className="w-8 h-8 mr-3 text-blue-600" />
                Versorger
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Verwaltung von Versorgern, Verträgen und Gerätschaften
              </p>
            </div>
            <div>
              <button
                onClick={() => setShowRoadmapModal(true)}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                <Presentation className="h-4 w-4 mr-2" />
                Roadmap
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-slate-900 p-6 rounded-lg shadow mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Suche</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Name, Typ, Kontakt..."
                  className="pl-10 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Eigentümer</label>
              <select
                value={selectedOwner}
                onChange={(e) => {
                  setSelectedOwner(e.target.value);
                  setSelectedProperty('');
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
              >
                <option value="">Alle Eigentümer</option>
                {owners.map(owner => (
                  <option key={owner.id} value={owner.id}>{owner.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Immobilie</label>
              <select
                value={selectedProperty}
                onChange={(e) => setSelectedProperty(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
              >
                <option value="">Alle Immobilien</option>
                {properties
                  .filter(property => !selectedOwner || property.owner_id === selectedOwner)
                  .map(property => (
                    <option key={property.id} value={property.id}>{property.property_name}</option>
                  ))}
              </select>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              {[
                { id: 'suppliers', label: 'Versorger', count: filteredSuppliers.length },
                { id: 'contracts', label: 'Verträge', count: filteredPropertySuppliers.length },
                { id: 'equipment', label: 'Gerätschaften', count: filteredEquipment.length },
                { id: 'messdienst', label: 'Digitaler Messdienst', count: 0 }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Suppliers Tab */}
            {activeTab === 'suppliers' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Versorger</h2>
                  <div className="flex items-center space-x-3">
                    {/* View Toggle */}
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setViewMode('table')}
                        className={`p-2 rounded ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-slate-800 text-gray-600 dark:text-gray-400'}`}
                      >
                        <List className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setViewMode('cards')}
                        className={`p-2 rounded ${viewMode === 'cards' ? 'bg-blue-600 text-white' : 'bg-gray-200 dark:bg-slate-800 text-gray-600 dark:text-gray-400'}`}
                      >
                        <Grid className="h-4 w-4" />
                      </button>
                    </div>
                    <button
                      onClick={() => {
                        setEditingItem(null);
                        setSupplierForm({ name: '', type: '', contact_person: '', phone: '', email: '', website: '', address: '', notes: '' });
                        setShowSupplierModal(true);
                      }}
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Versorger hinzufügen
                    </button>
                  </div>
                </div>

                {viewMode === 'cards' ? renderSuppliersCards() : renderSuppliersTable()}
              </div>
            )}

            {/* Contracts Tab */}
            {activeTab === 'contracts' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Verträge</h2>
                  <button
                    onClick={() => {
                      setEditingItem(null);
                      setContractForm({ property_id: '', supplier_id: '', contract_number: '', start_date: '', end_date: '', monthly_cost: '', notes: '' });
                      setShowContractModal(true);
                    }}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Vertrag hinzufügen
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-slate-800">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Immobilie</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Versorger</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Vertragsnummer</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Laufzeit</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Monatliche Kosten</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredPropertySuppliers.map((contract) => (
                        <tr key={contract.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            {contract.properties?.property_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            {contract.suppliers?.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            {contract.contract_number}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            {contract.start_date} - {contract.end_date}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            {contract.monthly_cost ? `€${contract.monthly_cost}` : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => {
                                  setEditingItem(contract);
                                  setContractForm(contract);
                                  setShowContractModal(true);
                                }}
                                className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteContract(contract.id)}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Equipment Tab */}
            {activeTab === 'equipment' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Gerätschaften</h2>
                  <button
                    onClick={() => {
                      setEditingItem(null);
                      setEquipmentForm({ property_id: '', supplier_id: '', unit_id: '', type: '', brand: '', model: '', serial_number: '', installation_date: '', last_reading: '', last_maintenance_date: '', location: '', status: 'active', notes: '' });
                      setShowEquipmentModal(true);
                    }}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Gerätschaft hinzufügen
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-slate-800">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Typ</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Marke/Modell</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Seriennummer</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Immobilie</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Einheit</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredEquipment.map((item) => (
                        <tr key={item.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            {item.type}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            {item.brand} {item.model}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            {item.serial_number}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            {item.properties?.property_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                            {item.units?.unit_identifier}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              item.status === 'active' 
                                ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                                : item.status === 'maintenance'
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100'
                                : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
                            }`}>
                              {item.status === 'active' ? 'Aktiv' : item.status === 'maintenance' ? 'Wartung' : 'Inaktiv'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => {
                                  setEditingItem(item);
                                  setEquipmentForm(item);
                                  setShowEquipmentModal(true);
                                }}
                                className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteEquipment(item.id)}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Messdienst Tab */}
            {activeTab === 'messdienst' && (
              <MessdienstDashboard 
                selectedProperty={selectedProperty}
                onNavigate={(action) => {
                  console.log('Messdienst navigation:', action);
                }}
              />
            )}
          </div>
        </div>

        {/* Supplier Modal */}
        {showSupplierModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-900 rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {editingItem ? 'Versorger bearbeiten' : 'Neuen Versorger hinzufügen'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Name</label>
                  <input
                    type="text"
                    value={supplierForm.name}
                    onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Typ</label>
                  <select
                    value={supplierForm.type}
                    onChange={(e) => setSupplierForm({ ...supplierForm, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  >
                    <option value="">Typ auswählen</option>
                    <option value="Strom">Strom</option>
                    <option value="Gas">Gas</option>
                    <option value="Wasser">Wasser</option>
                    <option value="Fernwärme">Fernwärme</option>
                    <option value="Internet">Internet</option>
                    <option value="Messdienstleister">Messdienstleister</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Kontaktperson</label>
                  <input
                    type="text"
                    value={supplierForm.contact_person}
                    onChange={(e) => setSupplierForm({ ...supplierForm, contact_person: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Telefon</label>
                  <input
                    type="text"
                    value={supplierForm.phone}
                    onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">E-Mail</label>
                  <input
                    type="email"
                    value={supplierForm.email}
                    onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Website</label>
                  <input
                    type="url"
                    value={supplierForm.website}
                    onChange={(e) => setSupplierForm({ ...supplierForm, website: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Adresse</label>
                  <textarea
                    value={supplierForm.address}
                    onChange={(e) => setSupplierForm({ ...supplierForm, address: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Notizen</label>
                  <textarea
                    value={supplierForm.notes}
                    onChange={(e) => setSupplierForm({ ...supplierForm, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowSupplierModal(false);
                    setEditingItem(null);
                    setSupplierForm({ name: '', type: '', contact_person: '', phone: '', email: '', website: '', address: '', notes: '' });
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleSaveSupplier}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Speichern
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Contract Modal */}
        {showContractModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-900 rounded-lg p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {editingItem ? 'Vertrag bearbeiten' : 'Neuen Vertrag hinzufügen'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Immobilie</label>
                  <select
                    value={contractForm.property_id}
                    onChange={(e) => setContractForm({ ...contractForm, property_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  >
                    <option value="">Immobilie auswählen</option>
                    {properties.map(property => (
                      <option key={property.id} value={property.id}>{property.property_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Versorger</label>
                  <select
                    value={contractForm.supplier_id}
                    onChange={(e) => setContractForm({ ...contractForm, supplier_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  >
                    <option value="">Versorger auswählen</option>
                    {suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Vertragsnummer</label>
                  <input
                    type="text"
                    value={contractForm.contract_number}
                    onChange={(e) => setContractForm({ ...contractForm, contract_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Startdatum</label>
                  <input
                    type="date"
                    value={contractForm.start_date}
                    onChange={(e) => setContractForm({ ...contractForm, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Enddatum</label>
                  <input
                    type="date"
                    value={contractForm.end_date}
                    onChange={(e) => setContractForm({ ...contractForm, end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Monatliche Kosten</label>
                  <input
                    type="number"
                    step="0.01"
                    value={contractForm.monthly_cost}
                    onChange={(e) => setContractForm({ ...contractForm, monthly_cost: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Notizen</label>
                  <textarea
                    value={contractForm.notes}
                    onChange={(e) => setContractForm({ ...contractForm, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowContractModal(false);
                    setEditingItem(null);
                    setContractForm({ property_id: '', supplier_id: '', contract_number: '', start_date: '', end_date: '', monthly_cost: '', notes: '' });
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleSaveContract}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Speichern
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Equipment Modal */}
        {showEquipmentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-900 rounded-lg p-6 w-full max-w-2xl">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {editingItem ? 'Gerätschaft bearbeiten' : 'Neue Gerätschaft hinzufügen'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Immobilie</label>
                  <select
                    value={equipmentForm.property_id}
                    onChange={(e) => setEquipmentForm({ ...equipmentForm, property_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  >
                    <option value="">Immobilie auswählen</option>
                    {properties.map(property => (
                      <option key={property.id} value={property.id}>{property.property_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Versorger</label>
                  <select
                    value={equipmentForm.supplier_id}
                    onChange={(e) => setEquipmentForm({ ...equipmentForm, supplier_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  >
                    <option value="">Versorger auswählen</option>
                    {suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Einheit</label>
                  <select
                    value={equipmentForm.unit_id}
                    onChange={(e) => setEquipmentForm({ ...equipmentForm, unit_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  >
                    <option value="">Einheit auswählen</option>
                    {units
                      .filter(unit => !equipmentForm.property_id || unit.property_id === equipmentForm.property_id)
                      .map(unit => (
                        <option key={unit.id} value={unit.id}>{unit.unit_identifier}</option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Typ</label>
                  <input
                    type="text"
                    value={equipmentForm.type}
                    onChange={(e) => setEquipmentForm({ ...equipmentForm, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Marke</label>
                  <input
                    type="text"
                    value={equipmentForm.brand}
                    onChange={(e) => setEquipmentForm({ ...equipmentForm, brand: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Modell</label>
                  <input
                    type="text"
                    value={equipmentForm.model}
                    onChange={(e) => setEquipmentForm({ ...equipmentForm, model: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Seriennummer</label>
                  <input
                    type="text"
                    value={equipmentForm.serial_number}
                    onChange={(e) => setEquipmentForm({ ...equipmentForm, serial_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Installationsdatum</label>
                  <input
                    type="date"
                    value={equipmentForm.installation_date}
                    onChange={(e) => setEquipmentForm({ ...equipmentForm, installation_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Letzter Zählerstand</label>
                  <input
                    type="text"
                    value={equipmentForm.last_reading}
                    onChange={(e) => setEquipmentForm({ ...equipmentForm, last_reading: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Letzte Wartung</label>
                  <input
                    type="date"
                    value={equipmentForm.last_maintenance_date}
                    onChange={(e) => setEquipmentForm({ ...equipmentForm, last_maintenance_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Standort</label>
                  <input
                    type="text"
                    value={equipmentForm.location}
                    onChange={(e) => setEquipmentForm({ ...equipmentForm, location: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
                  <select
                    value={equipmentForm.status}
                    onChange={(e) => setEquipmentForm({ ...equipmentForm, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  >
                    <option value="active">Aktiv</option>
                    <option value="maintenance">Wartung</option>
                    <option value="inactive">Inaktiv</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Notizen</label>
                  <textarea
                    value={equipmentForm.notes}
                    onChange={(e) => setEquipmentForm({ ...equipmentForm, notes: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowEquipmentModal(false);
                    setEditingItem(null);
                    setEquipmentForm({ property_id: '', supplier_id: '', unit_id: '', type: '', brand: '', model: '', serial_number: '', installation_date: '', last_reading: '', last_maintenance_date: '', location: '', status: 'active', notes: '' });
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleSaveEquipment}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Speichern
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Roadmap Modal */}
        {showRoadmapModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl w-full max-w-6xl h-5/6 flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Clara360 Roadmap - Slide {currentSlide + 1} von 19
                </h2>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => window.open('/clara360_messdienst_praesentation', '_blank')}
                    className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    title="PDF Download"
                  >
                    <Download className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setShowRoadmapModal(false)}
                    className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="flex-1 p-6 overflow-hidden">
                  <div className="h-full rounded-lg overflow-auto">
                    {getSlideContent(currentSlide)}
                  </div>
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
                  disabled={currentSlide === 0}
                  className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Zurück
                </button>
                
                <div className="flex space-x-1">
                  {Array.from({ length: 19 }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentSlide(i)}
                      className={`w-3 h-3 rounded-full ${
                        i === currentSlide ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    />
                  ))}
                </div>

                <button
                  onClick={() => setCurrentSlide(Math.min(18, currentSlide + 1))}
                  disabled={currentSlide === 18}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Weiter
                  <ChevronRight className="h-4 w-4 ml-2" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Helper function for slide content as JSX components
  function getSlideContent(slideIndex) {
    const slides = [
      // Slide 1: Titelseite
      <div className="flex flex-col items-center justify-center text-center space-y-12 w-full h-full bg-gradient-to-br from-blue-600 to-blue-900 text-white p-16">
        <div className="text-6xl font-bold mb-8">🚀</div>
        <h1 className="text-6xl font-bold">Clara360 Messdienst-Revolution</h1>
        <h2 className="text-3xl font-medium">Wie wir Techem, ISTA & Co. angreifen</h2>
        <p className="text-xl mt-16">September 2025</p>
      </div>,
      
      // Slide 2: Marktchance
      <div className="p-12 h-full bg-white dark:bg-slate-900">
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-blue-600 dark:text-blue-400">Marktchance</h1>
          <h2 className="text-2xl font-medium mt-2 text-gray-800 dark:text-gray-200">€8 Mrd. Messdienst-Markt in Deutschland</h2>
        </div>
        
        <div className="flex-grow flex flex-col items-center justify-center">
          <div className="mb-8 text-8xl text-blue-500">📊</div>
          
          <div className="grid grid-cols-2 gap-8 w-full mt-4">
            <div className="text-lg text-gray-700 dark:text-gray-300">
              <p className="mb-2">📈 <span className="font-semibold text-blue-600 dark:text-blue-400">Wachstumstreiber:</span> Gesetzliche Vorgaben zur Fernablesung seit 2020</p>
              <p className="mb-2">📅 <span className="font-semibold text-blue-600 dark:text-blue-400">Nachrüstungsfrist:</span> Bis Ende 2026 müssen alle Zähler fernablesbar sein</p>
            </div>
            <div className="text-lg text-gray-700 dark:text-gray-300">
              <p className="mb-2">🏭 <span className="font-semibold text-blue-600 dark:text-blue-400">Marktkonzentration:</span> Techem und ISTA mit 50-60% Marktanteil</p>
              <p className="mb-2">🎯 <span className="font-semibold text-blue-600 dark:text-blue-400">Chance für Clara360:</span> Digitalisierung und Kostendruck</p>
            </div>
          </div>
        </div>
      </div>,
      
      // Slide 3: Wettbewerbsanalyse
      <div className="p-12 h-full bg-white dark:bg-slate-900">
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-blue-600 dark:text-blue-400">Wettbewerbsanalyse</h1>
          <h2 className="text-2xl font-medium mt-2 text-gray-800 dark:text-gray-200">Clara360 vs. Techem, ISTA und Brunata</h2>
        </div>
        
        <div className="flex-grow flex flex-col">
          <div className="overflow-x-auto mb-6">
            <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
              <thead>
                <tr className="bg-blue-600 text-white">
                  <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left">Kriterium</th>
                  <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-center">Clara360</th>
                  <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-center">Techem</th>
                  <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-center">ISTA</th>
                  <th className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-center">Brunata</th>
                </tr>
              </thead>
              <tbody className="text-center">
                <tr>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left text-gray-800 dark:text-gray-200">Kosten pro Einheit/Jahr</td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 bg-green-500 text-white font-bold">8-25€</td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 bg-red-500 text-white">30-40€</td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 bg-red-500 text-white">30-40€</td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 bg-red-500 text-white">28-38€</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left text-gray-800 dark:text-gray-200">Vertragslaufzeit</td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 bg-green-500 text-white font-bold">Jährlich kündbar</td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 bg-red-500 text-white">10-15 Jahre</td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 bg-red-500 text-white">10-15 Jahre</td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 bg-red-500 text-white">5-10 Jahre</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left text-gray-800 dark:text-gray-200">Hardware-Eigentum</td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 bg-green-500 text-white font-bold">Eigentümer</td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 bg-red-500 text-white">Geleast</td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 bg-red-500 text-white">Geleast</td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 bg-yellow-500 text-gray-800">Teilweise</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left text-gray-800 dark:text-gray-200">Digitalisierungsgrad</td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 bg-green-500 text-white font-bold">Vollständig</td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 bg-yellow-500 text-gray-800">Teilweise</td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 bg-yellow-500 text-gray-800">Teilweise</td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 bg-red-500 text-white">Gering</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 text-left text-gray-800 dark:text-gray-200">Integration in Verwaltung</td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 bg-green-500 text-white font-bold">Vollständig</td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 bg-red-500 text-white">Keine</td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 bg-red-500 text-white">Keine</td>
                  <td className="border border-gray-300 dark:border-gray-600 px-4 py-3 bg-red-500 text-white">Keine</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="grid grid-cols-2 gap-8 mt-4">
            <div className="text-lg text-gray-700 dark:text-gray-300">
              <p className="mb-2">📊 <span className="font-semibold text-blue-600 dark:text-blue-400">Marktkonzentration:</span> Top 5 Anbieter haben 80% Marktanteil</p>
              <p className="mb-2">🔒 <span className="font-semibold text-blue-600 dark:text-blue-400">Stärke der Etablierten:</span> Langfristige Verträge, Kundenbindung</p>
            </div>
            <div className="text-lg text-gray-700 dark:text-gray-300">
              <p className="mb-2">⚠️ <span className="font-semibold text-blue-600 dark:text-blue-400">Schwäche der Etablierten:</span> Hohe Kosten, geringe Flexibilität</p>
              <p className="mb-2">💡 <span className="font-semibold text-blue-600 dark:text-blue-400">Clara360 Vorteil:</span> Moderne Technologie, Kosteneffizienz</p>
            </div>
          </div>
        </div>
      </div>,
      
      // Slide 4: Kostenstruktur
      <div className="p-12 h-full bg-white dark:bg-slate-900">
        <h1 className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-8">Kostenstruktur entlarven</h1>
        <h2 className="text-2xl font-medium mb-8 text-gray-600 dark:text-gray-300">Transparente Preise vs. versteckte Kosten</h2>
        
        <div className="grid grid-cols-2 gap-12 h-full">
          <div className="bg-red-50 dark:bg-red-900 p-8 rounded-lg border-2 border-red-200 dark:border-red-700">
            <h3 className="text-2xl font-bold text-red-600 dark:text-red-300 mb-6">💸 Versteckte Kosten bei Techem/ISTA:</h3>
            <div className="space-y-4 text-lg text-red-700 dark:text-red-300">
              <p>• Anfahrtskosten (64,86€ bei ISTA)</p>
              <p>• Dateneinrichtung (1,90€ pro Gerät)</p>
              <p>• Serviceaufschläge für Sonderleistungen</p>
              <p>• Hardware-Leasing statt Kauf</p>
            </div>
          </div>
          
          <div className="bg-green-50 dark:bg-green-900 p-8 rounded-lg border-2 border-green-200 dark:border-green-700">
            <h3 className="text-2xl font-bold text-green-600 dark:text-green-300 mb-6">✅ Clara360 Preisvorteile:</h3>
            <div className="space-y-4 text-lg text-green-700 dark:text-green-300">
              <p>• Transparente Festpreise ohne Aufschläge</p>
              <p>• Hardware im Eigentum statt Leasing</p>
              <p>• Keine versteckten Servicekosten</p>
              <p>• Jährlich kündbar ohne Langzeitbindung</p>
            </div>
          </div>
        </div>
        
        <div className="text-center mt-8">
          <div className="bg-gradient-to-r from-green-500 to-blue-500 text-white p-6 rounded-xl">
            <div className="text-3xl font-bold">Sparen Sie 60-80% der Kosten nach 2-3 Jahren mit Clara360</div>
          </div>
        </div>
      </div>,
      
      // Slide 5: Etablierte vs. Innovation (Faire Darstellung)
      <div className="p-6 h-full bg-white dark:bg-slate-900">
        <div className="mb-4 text-center">
          <h1 className="text-3xl font-bold text-blue-600 dark:text-blue-400">Etablierte Anbieter vs. Clara360</h1>
          <h2 className="text-xl font-medium mt-2 text-gray-800 dark:text-gray-200">Bewährte Lösungen vs. Moderne Innovation</h2>
        </div>
        
        <div className="flex-grow grid grid-cols-3 gap-6 items-center justify-evenly">
          <div className="bg-gradient-to-br from-gray-600 to-gray-800 text-white rounded-xl p-6 relative min-h-96">
            <div className="absolute -top-1 -right-1 bg-orange-500 text-white px-2 py-1 rounded-full font-bold text-xs transform rotate-12">ETABLIERT</div>
            <h3 className="text-2xl font-bold text-center mb-4">
              😞 Techem/ISTA/Brunata
            </h3>
            
            <div className="text-6xl text-center mb-6">📋</div>
            
            <ul className="space-y-3">
              <li className="flex items-center">
                <span className="text-green-400 mr-2">✅</span>
                Haben auch Portale & Funkmelder
              </li>
              <li className="flex items-center">
                <span className="text-yellow-400 mr-2">⚠️</span>
                Teilweise noch Papier-Zettel
              </li>
              <li className="flex items-center">
                <span className="text-yellow-400 mr-2">⚠️</span>
                5 Jahre Bindung
              </li>
              <li className="flex items-center">
                <span className="text-red-400 mr-2">❌</span>
                30-40€/Einheit/Monat
              </li>
            </ul>
            
            <div className="text-center mt-4">
              <span className="bg-red-600 px-3 py-1 rounded-full font-bold text-sm">
                💸 TEUER & VERALTET
              </span>
            </div>
          </div>
          
          <div className="flex justify-center">
            <div className="bg-gradient-to-br from-red-500 to-orange-500 text-white text-4xl font-black rounded-full w-20 h-20 flex items-center justify-center shadow-lg animate-pulse">
              VS
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-blue-600 to-blue-900 text-white rounded-xl p-6 relative min-h-96">
            <div className="absolute -top-1 -right-1 bg-green-500 text-white px-2 py-1 rounded-full font-bold text-xs transform rotate-12">INNOVATION</div>
            <h3 className="text-2xl font-bold text-center mb-4">
              😊 Clara360 Messdienst
            </h3>
            
            <div className="text-6xl text-center mb-6">🚀</div>
            
            <ul className="space-y-3">
              <li className="flex items-center">
                <span className="text-green-400 mr-2">✅</span>
                Echtzeit-Dashboard
              </li>
              <li className="flex items-center">
                <span className="text-green-400 mr-2">✅</span>
                KI-Auswertung
              </li>
              <li className="flex items-center">
                <span className="text-green-400 mr-2">✅</span>
                Sofort digital
              </li>
              <li className="flex items-center">
                <span className="text-green-400 mr-2">✅</span>
                3 Monate kündbar
              </li>
              <li className="flex items-center">
                <span className="text-green-400 mr-2">✅</span>
                8-25€/Einheit/Monat
              </li>
            </ul>
            
            <div className="text-center mt-4">
              <span className="bg-green-600 px-3 py-1 rounded-full font-bold text-sm">
                🚀 SMART & GÜNSTIG
              </span>
            </div>
          </div>
        </div>
        
        <div className="mt-6 text-center">
          <div className="bg-gradient-to-r from-blue-500 to-green-500 text-white p-4 rounded-xl">
            <h3 className="text-xl font-bold mb-1">
              Respekt vor etablierten Anbietern – Innovation für die Zukunft!
            </h3>
            <p className="text-lg">
              Warum mehr zahlen, wenn moderne Technologie günstiger und flexibler ist? 💡
            </p>
          </div>
        </div>
      </div>,
      
      // Slide 6: Einsparungsrechnung
      <div className="p-8 h-full bg-white dark:bg-slate-900">
        <div className="mb-4 text-center">
          <h1 className="text-4xl font-bold text-blue-600 dark:text-blue-400">Konkrete Einsparungsrechnung</h1>
          <h2 className="text-2xl font-medium mt-2 text-gray-800 dark:text-gray-200">50 Einheiten = 20.000€ Ersparnis in 2 Jahren</h2>
        </div>
        
        <div className="flex-grow grid grid-cols-2 gap-8">
          <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow-lg border-l-4 border-blue-500">
            <h3 className="text-xl font-bold mb-4 text-center text-gray-800 dark:text-gray-200">Rechenbeispiel: 50 Einheiten</h3>
            
            <div className="space-y-4">
              <div className="flex items-center">
                <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 font-bold">1</div>
                <div className="text-gray-700 dark:text-gray-300">
                  <div className="font-medium">Techem/ISTA:</div>
                  <div>50 × 35€ × 24 Mon. = <span className="text-blue-600 dark:text-blue-400 font-bold">42.000€</span></div>
                </div>
              </div>
              
              <div className="flex items-center">
                <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 font-bold">2</div>
                <div className="text-gray-700 dark:text-gray-300">
                  <div className="font-medium">Clara360:</div>
                  <div>50 × 15€ × 24 Mon. = <span className="text-blue-600 dark:text-blue-400 font-bold">18.000€</span></div>
                </div>
              </div>
              
              <div className="flex items-center">
                <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 font-bold">3</div>
                <div className="text-gray-700 dark:text-gray-300">
                  <div className="font-medium">Hardware:</div>
                  <div>50 × 80€ = <span className="text-blue-600 dark:text-blue-400 font-bold">4.000€</span></div>
                </div>
              </div>
              
              <hr className="border-gray-300 dark:border-gray-600" />
              
              <div className="flex items-center">
                <div className="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3 font-bold">💰</div>
                <div className="text-gray-700 dark:text-gray-300">
                  <div className="font-bold">Gesamtersparnis:</div>
                  <div className="text-green-600 font-bold text-lg">42.000€ - 22.000€ = 20.000€</div>
                </div>
              </div>
              
              <div className="text-center bg-green-100 dark:bg-green-900 p-3 rounded-lg">
                <div className="text-green-800 dark:text-green-200 font-bold text-xl">48% weniger Kosten!</div>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="bg-red-500 text-white rounded-lg p-4 text-center">
              <div className="text-2xl font-bold">42.000€</div>
              <div className="text-sm opacity-90">Techem/ISTA (2 Jahre)</div>
              <div className="mt-1 text-sm">❌ Teuer & unflexibel</div>
            </div>
            
            <div className="bg-blue-500 text-white rounded-lg p-4 text-center">
              <div className="text-2xl font-bold">22.000€</div>
              <div className="text-sm opacity-90">Clara360 (2 Jahre)</div>
              <div className="mt-1 text-sm">✅ Smart & günstig</div>
            </div>
            
            <div className="bg-green-500 text-white rounded-lg p-4 text-center">
              <div className="text-2xl font-bold">20.000€</div>
              <div className="text-sm opacity-90">Ihre Ersparnis</div>
              <div className="mt-1 text-sm">🐷 Direkt gespart</div>
            </div>
          </div>
        </div>
      </div>,
      
      // Slide 7: Mieterfreundlich
      <div className="p-8 h-full bg-white dark:bg-slate-900">
        <div className="mb-6 text-center">
          <h1 className="text-4xl font-bold text-blue-600 dark:text-blue-400">Mieterfreundlich</h1>
          <h2 className="text-2xl font-medium mt-2 text-gray-800 dark:text-gray-200">Keine Termine, kein Warten – Daten sofort digital</h2>
        </div>
        
        <div className="flex-grow grid grid-cols-2 gap-8">
          <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white rounded-lg p-6">
            <h3 className="text-2xl font-bold text-center mb-6">Mieter-Erfahrung: Alt vs. Neu</h3>
            
            <div className="space-y-4">
              <div className="flex items-center">
                <span className="text-red-400 mr-3 text-xl">❌</span>
                <span className="line-through opacity-75">Termine vereinbaren & warten</span>
              </div>
              <div className="flex items-center">
                <span className="text-green-400 mr-3 text-xl">✅</span>
                <span>Automatische Datenübertragung 24/7</span>
              </div>
              
              <div className="flex items-center">
                <span className="text-red-400 mr-3 text-xl">❌</span>
                <span className="line-through opacity-75">Fremde Personen in der Wohnung</span>
              </div>
              <div className="flex items-center">
                <span className="text-green-400 mr-3 text-xl">✅</span>
                <span>Keine Störungen, keine Besuche</span>
              </div>
              
              <div className="flex items-center">
                <span className="text-red-400 mr-3 text-xl">❌</span>
                <span className="line-through opacity-75">Wochen warten auf Abrechnung</span>
              </div>
              <div className="flex items-center">
                <span className="text-green-400 mr-3 text-xl">✅</span>
                <span>Echtzeit-Verbrauchsdaten im Portal</span>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white dark:bg-slate-800 rounded-lg p-4 text-center shadow-lg">
              <div className="text-4xl text-blue-500 mb-3">🕐</div>
              <div className="font-bold text-gray-800 dark:text-gray-200">24/7 Verfügbar</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Verbrauchsdaten jederzeit abrufbar</div>
            </div>
            
            <div className="bg-white dark:bg-slate-800 rounded-lg p-4 text-center shadow-lg">
              <div className="text-4xl text-blue-500 mb-3">🛡️</div>
              <div className="font-bold text-gray-800 dark:text-gray-200">Datenschutz</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">DSGVO-konforme Verarbeitung</div>
            </div>
            
            <div className="bg-white dark:bg-slate-800 rounded-lg p-4 text-center shadow-lg">
              <div className="text-4xl text-blue-500 mb-3">📱</div>
              <div className="font-bold text-gray-800 dark:text-gray-200">Mobile App</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Verbrauch unterwegs checken</div>
            </div>
            
            <div className="bg-white dark:bg-slate-800 rounded-lg p-4 text-center shadow-lg">
              <div className="text-4xl text-blue-500 mb-3">🌱</div>
              <div className="font-bold text-gray-800 dark:text-gray-200">Nachhaltigkeit</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Verbrauch optimieren & CO₂ sparen</div>
            </div>
          </div>
        </div>
      </div>,
      
      // Slide 8: Nachhaltigkeit & ESG
      <div className="p-8 h-full bg-white dark:bg-slate-900">
        <div className="mb-6 text-center">
          <h1 className="text-4xl font-bold text-green-600 dark:text-green-400">Nachhaltigkeit & ESG</h1>
          <h2 className="text-2xl font-medium mt-2 text-gray-800 dark:text-gray-200">15-25% CO₂-Reduktion durch intelligente Verbrauchsoptimierung</h2>
        </div>
        
        <div className="flex-grow grid grid-cols-3 gap-6">
          <div className="bg-green-50 dark:bg-green-900 rounded-lg p-6 text-center">
            <div className="text-5xl text-green-500 mb-4">🌍</div>
            <h3 className="text-xl font-bold text-green-700 dark:text-green-300 mb-3">Environmental</h3>
            <ul className="text-sm text-green-600 dark:text-green-400 space-y-2">
              <li>• 15-25% weniger CO₂-Emissionen</li>
              <li>• Optimierter Energieverbrauch</li>
              <li>• Digitale Prozesse statt Papier</li>
              <li>• Weniger Anfahrten = weniger Verkehr</li>
            </ul>
          </div>
          
          <div className="bg-blue-50 dark:bg-blue-900 rounded-lg p-6 text-center">
            <div className="text-5xl text-blue-500 mb-4">👥</div>
            <h3 className="text-xl font-bold text-blue-700 dark:text-blue-300 mb-3">Social</h3>
            <ul className="text-sm text-blue-600 dark:text-blue-400 space-y-2">
              <li>• Transparenz für alle Mieter</li>
              <li>• Keine Störungen durch Ableser</li>
              <li>• Faire Kostenverteilung</li>
              <li>• Digitale Teilhabe fördern</li>
            </ul>
          </div>
          
          <div className="bg-purple-50 dark:bg-purple-900 rounded-lg p-6 text-center">
            <div className="text-5xl text-purple-500 mb-4">⚖️</div>
            <h3 className="text-xl font-bold text-purple-700 dark:text-purple-300 mb-3">Governance</h3>
            <ul className="text-sm text-purple-600 dark:text-purple-400 space-y-2">
              <li>• DSGVO-konforme Datenverarbeitung</li>
              <li>• Transparente Preisgestaltung</li>
              <li>• Ethische Geschäftspraktiken</li>
              <li>• Langfristige Partnerschaften</li>
            </ul>
          </div>
        </div>
        
        <div className="mt-6 bg-gradient-to-r from-green-500 to-blue-500 text-white p-6 rounded-xl text-center">
          <h3 className="text-2xl font-bold mb-2">🎯 ESG-Ziele erreichen mit Clara360</h3>
          <p className="text-lg">Nachhaltigkeit, soziale Verantwortung und gute Unternehmensführung in einem System!</p>
        </div>
      </div>,
      
      // Slide 9: Unique Advantage
      <div className="p-8 h-full bg-white dark:bg-slate-900">
        <div className="mb-6 text-center">
          <h1 className="text-4xl font-bold text-blue-600 dark:text-blue-400">Unique Advantage</h1>
          <h2 className="text-2xl font-medium mt-2 text-gray-800 dark:text-gray-200">Komplettlösung aus einer Hand</h2>
        </div>
        
        <div className="flex-grow grid grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="bg-blue-50 dark:bg-blue-900 rounded-lg p-6">
              <h3 className="text-xl font-bold text-blue-700 dark:text-blue-300 mb-4">🎯 Einzigartiger Ansatz</h3>
              <ul className="space-y-3 text-blue-600 dark:text-blue-400">
                <li>• Messdienst + Hausverwaltung in einem System</li>
                <li>• Nahtlose Integration aller Prozesse</li>
                <li>• Ein Ansprechpartner für alles</li>
                <li>• Keine Schnittstellen-Probleme</li>
              </ul>
            </div>
            
            <div className="bg-green-50 dark:bg-green-900 rounded-lg p-6">
              <h3 className="text-xl font-bold text-green-700 dark:text-green-300 mb-4">⚡ Technologie-Vorsprung</h3>
              <ul className="space-y-3 text-green-600 dark:text-green-400">
                <li>• LoRaWAN-first Architektur</li>
                <li>• KI-basierte Anomalie-Erkennung</li>
                <li>• Predictive Analytics</li>
                <li>• Open Standards statt Vendor Lock-in</li>
              </ul>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-600 to-blue-600 text-white rounded-lg p-8 flex flex-col justify-center">
            <div className="text-center">
              <div className="text-6xl mb-6">🏆</div>
              <h3 className="text-2xl font-bold mb-4">Warum Clara360 gewinnt</h3>
              <div className="space-y-4 text-lg">
                <p>✅ <strong>Vollintegration</strong> statt Flickwerk</p>
                <p>✅ <strong>Moderne Technologie</strong> statt Legacy</p>
                <p>✅ <strong>Transparente Preise</strong> statt versteckte Kosten</p>
                <p>✅ <strong>Flexibilität</strong> statt Langzeit-Bindung</p>
              </div>
            </div>
          </div>
        </div>
      </div>,
      
      // Slide 10: Killer Features
      <div className="p-8 h-full bg-white dark:bg-slate-900">
        <div className="mb-6 text-center">
          <h1 className="text-4xl font-bold text-purple-600 dark:text-purple-400">Killer Features</h1>
          <h2 className="text-2xl font-medium mt-2 text-gray-800 dark:text-gray-200">KI, Predictive Analytics & Open Standards</h2>
        </div>
        
        <div className="flex-grow grid grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-purple-500 to-pink-500 text-white rounded-lg p-6 text-center">
            <div className="text-5xl mb-4">🤖</div>
            <h3 className="text-xl font-bold mb-3">KI-Anomalie-Erkennung</h3>
            <ul className="text-sm space-y-2">
              <li>• Automatische Leck-Erkennung</li>
              <li>• Ungewöhnliche Verbrauchsmuster</li>
              <li>• Defekte Geräte identifizieren</li>
              <li>• Proaktive Wartungshinweise</li>
            </ul>
          </div>
          
          <div className="bg-gradient-to-br from-blue-500 to-cyan-500 text-white rounded-lg p-6 text-center">
            <div className="text-5xl mb-4">📊</div>
            <h3 className="text-xl font-bold mb-3">Predictive Analytics</h3>
            <ul className="text-sm space-y-2">
              <li>• Verbrauchsprognosen</li>
              <li>• Kostenoptimierung</li>
              <li>• Wartungsplanung</li>
              <li>• Budgetplanung für Verwalter</li>
            </ul>
          </div>
          
          <div className="bg-gradient-to-br from-green-500 to-teal-500 text-white rounded-lg p-6 text-center">
            <div className="text-5xl mb-4">🔓</div>
            <h3 className="text-xl font-bold mb-3">Open Standards</h3>
            <ul className="text-sm space-y-2">
              <li>• Kein Vendor Lock-in</li>
              <li>• Standard-Protokolle</li>
              <li>• Herstellerunabhängig</li>
              <li>• Zukunftssicher</li>
            </ul>
          </div>
        </div>
        
        <div className="mt-6 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-xl text-center">
          <h3 className="text-2xl font-bold mb-2">🚀 Innovation trifft Praxis</h3>
          <p className="text-lg">Modernste Technologie für maximale Effizienz und Kosteneinsparung!</p>
        </div>
      </div>,
      
      // Slide 11: Revenue Model
      <div className="p-8 h-full bg-white dark:bg-slate-900">
        <div className="mb-6 text-center">
          <h1 className="text-4xl font-bold text-green-600 dark:text-green-400">Revenue Model</h1>
          <h2 className="text-2xl font-medium mt-2 text-gray-800 dark:text-gray-200">SaaS + Hardware + Services</h2>
        </div>
        
        <div className="flex-grow grid grid-cols-3 gap-6">
          <div className="bg-blue-50 dark:bg-blue-900 rounded-lg p-6 text-center">
            <div className="text-4xl text-blue-500 mb-4">💻</div>
            <h3 className="text-xl font-bold text-blue-700 dark:text-blue-300 mb-3">SaaS Platform</h3>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-2">8-15€</div>
            <div className="text-sm text-blue-600 dark:text-blue-400 mb-4">pro Einheit/Monat</div>
            <ul className="text-sm text-blue-600 dark:text-blue-400 space-y-1">
              <li>• Portal & Dashboard</li>
              <li>• KI-Analytics</li>
              <li>• Support & Updates</li>
              <li>• API-Zugang</li>
            </ul>
          </div>
          
          <div className="bg-orange-50 dark:bg-orange-900 rounded-lg p-6 text-center">
            <div className="text-4xl text-orange-500 mb-4">📡</div>
            <h3 className="text-xl font-bold text-orange-700 dark:text-orange-300 mb-3">Hardware</h3>
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400 mb-2">60-120€</div>
            <div className="text-sm text-orange-600 dark:text-orange-400 mb-4">einmalig pro Gerät</div>
            <ul className="text-sm text-orange-600 dark:text-orange-400 space-y-1">
              <li>• LoRaWAN-Sensoren</li>
              <li>• Gateway-Hardware</li>
              <li>• Installation</li>
              <li>• 5 Jahre Garantie</li>
            </ul>
          </div>
          
          <div className="bg-purple-50 dark:bg-purple-900 rounded-lg p-6 text-center">
            <div className="text-4xl text-purple-500 mb-4">🛠️</div>
            <h3 className="text-xl font-bold text-purple-700 dark:text-purple-300 mb-3">Services</h3>
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 mb-2">5-10€</div>
            <div className="text-sm text-purple-600 dark:text-purple-400 mb-4">pro Einheit/Monat</div>
            <ul className="text-sm text-purple-600 dark:text-purple-400 space-y-1">
              <li>• Abrechnungsservice</li>
              <li>• Wartung & Kalibrierung</li>
              <li>• Compliance & Eichung</li>
              <li>• Premium Support</li>
            </ul>
          </div>
        </div>
        
        <div className="mt-6 bg-gradient-to-r from-green-500 to-blue-500 text-white p-6 rounded-xl text-center">
          <h3 className="text-2xl font-bold mb-2">💰 Skalierbare Umsatzströme</h3>
          <p className="text-lg">Wiederkehrende Einnahmen + Hardware-Verkauf + Premium-Services = nachhaltiges Wachstum</p>
        </div>
      </div>,
      
      // Slide 12: Roadmap
      <div className="p-8 h-full bg-white dark:bg-slate-900">
        <div className="mb-6 text-center">
          <h1 className="text-4xl font-bold text-blue-600 dark:text-blue-400">Roadmap</h1>
          <h2 className="text-2xl font-medium mt-2 text-gray-800 dark:text-gray-200">3-Phasen-Plan bis 2025</h2>
        </div>
        
        <div className="flex-grow grid grid-cols-3 gap-6">
          <div className="bg-green-50 dark:bg-green-900 rounded-lg p-6">
            <div className="text-center mb-4">
              <div className="bg-green-500 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2 text-xl font-bold">1</div>
              <h3 className="text-xl font-bold text-green-700 dark:text-green-300">Q4 2024 - Q1 2025</h3>
              <p className="text-sm text-green-600 dark:text-green-400">MVP & Pilotprojekte</p>
            </div>
            <ul className="text-sm text-green-600 dark:text-green-400 space-y-2">
              <li>✅ Portal-Integration fertigstellen</li>
              <li>✅ LoRaWAN-Hardware testen</li>
              <li>🔄 5 Pilotprojekte (250 Einheiten)</li>
              <li>🔄 Erste Kundenfeedbacks</li>
              <li>📅 Break-Even erreichen</li>
            </ul>
          </div>
          
          <div className="bg-blue-50 dark:bg-blue-900 rounded-lg p-6">
            <div className="text-center mb-4">
              <div className="bg-blue-500 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2 text-xl font-bold">2</div>
              <h3 className="text-xl font-bold text-blue-700 dark:text-blue-300">Q2 - Q3 2025</h3>
              <p className="text-sm text-blue-600 dark:text-blue-400">Skalierung & KI</p>
            </div>
            <ul className="text-sm text-blue-600 dark:text-blue-400 space-y-2">
              <li>📅 KI-Features ausrollen</li>
              <li>📅 50+ Kunden akquirieren</li>
              <li>📅 2.500 Einheiten verwalten</li>
              <li>📅 Mobile App launchen</li>
              <li>📅 API-Marketplace öffnen</li>
            </ul>
          </div>
          
          <div className="bg-purple-50 dark:bg-purple-900 rounded-lg p-6">
            <div className="text-center mb-4">
              <div className="bg-purple-500 text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2 text-xl font-bold">3</div>
              <h3 className="text-xl font-bold text-purple-700 dark:text-purple-300">Q4 2025+</h3>
              <p className="text-sm text-purple-600 dark:text-purple-400">Marktführerschaft</p>
            </div>
            <ul className="text-sm text-purple-600 dark:text-purple-400 space-y-2">
              <li>📅 10.000+ Einheiten</li>
              <li>📅 Expansion in andere Städte</li>
              <li>📅 Predictive Analytics</li>
              <li>📅 IoT-Ecosystem erweitern</li>
              <li>📅 Exit-Strategien evaluieren</li>
            </ul>
          </div>
        </div>
        
        <div className="mt-6 bg-gradient-to-r from-green-500 via-blue-500 to-purple-500 text-white p-6 rounded-xl text-center">
          <h3 className="text-2xl font-bold mb-2">🎯 Klarer Weg zum Erfolg</h3>
          <p className="text-lg">Von MVP zu Marktführer in 18 Monaten - mit messbaren Meilensteinen</p>
        </div>
      </div>,
      
      // Slide 13: Financials
      <div className="p-8 h-full bg-white dark:bg-slate-900">
        <div className="mb-6 text-center">
          <h1 className="text-4xl font-bold text-green-600 dark:text-green-400">Financials</h1>
          <h2 className="text-2xl font-medium mt-2 text-gray-800 dark:text-gray-200">Break-Even in 18-24 Monaten</h2>
        </div>
        
        <div className="flex-grow grid grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="bg-green-50 dark:bg-green-900 rounded-lg p-6">
              <h3 className="text-xl font-bold text-green-700 dark:text-green-300 mb-4">💰 Revenue Projections</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-green-600 dark:text-green-400">Jahr 1 (2025):</span>
                  <span className="font-bold text-green-700 dark:text-green-300">€180k</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-600 dark:text-green-400">Jahr 2 (2026):</span>
                  <span className="font-bold text-green-700 dark:text-green-300">€650k</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-600 dark:text-green-400">Jahr 3 (2027):</span>
                  <span className="font-bold text-green-700 dark:text-green-300">€1.2M</span>
                </div>
              </div>
            </div>
            
            <div className="bg-blue-50 dark:bg-blue-900 rounded-lg p-6">
              <h3 className="text-xl font-bold text-blue-700 dark:text-blue-300 mb-4">📊 Key Metrics</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-blue-600 dark:text-blue-400">Customer LTV:</span>
                  <span className="font-bold text-blue-700 dark:text-blue-300">€2.400</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-600 dark:text-blue-400">CAC:</span>
                  <span className="font-bold text-blue-700 dark:text-blue-300">€180</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-600 dark:text-blue-400">LTV/CAC Ratio:</span>
                  <span className="font-bold text-blue-700 dark:text-blue-300">13.3x</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-green-500 to-blue-500 text-white rounded-lg p-8 flex flex-col justify-center">
            <div className="text-center">
              <div className="text-6xl mb-6">📈</div>
              <h3 className="text-2xl font-bold mb-6">Investment Highlights</h3>
              <div className="space-y-4 text-lg">
                <p>💡 <strong>Recurring Revenue:</strong> 85% SaaS-Anteil</p>
                <p>🚀 <strong>Skalierbarkeit:</strong> Marginale Grenzkosten</p>
                <p>🎯 <strong>Market Fit:</strong> Bewiesene Nachfrage</p>
                <p>💰 <strong>Exit Potential:</strong> 5-8x Revenue Multiple</p>
              </div>
            </div>
          </div>
        </div>
      </div>,
      
      // Slide 14: Implementation
      <div className="p-8 h-full bg-white dark:bg-slate-900">
        <div className="mb-6 text-center">
          <h1 className="text-4xl font-bold text-purple-600 dark:text-purple-400">Implementation</h1>
          <h2 className="text-2xl font-medium mt-2 text-gray-800 dark:text-gray-200">Team, Tech-Stack & Timeline</h2>
        </div>
        
        <div className="flex-grow grid grid-cols-3 gap-6">
          <div className="bg-purple-50 dark:bg-purple-900 rounded-lg p-6">
            <div className="text-4xl text-purple-500 mb-4 text-center">👥</div>
            <h3 className="text-xl font-bold text-purple-700 dark:text-purple-300 mb-4 text-center">Team</h3>
            <ul className="text-sm text-purple-600 dark:text-purple-400 space-y-2">
              <li>• <strong>CTO:</strong> IoT & LoRaWAN Experte</li>
              <li>• <strong>Lead Dev:</strong> Full-Stack (React/Node)</li>
              <li>• <strong>Hardware:</strong> Embedded Systems</li>
              <li>• <strong>Sales:</strong> PropTech Erfahrung</li>
              <li>• <strong>Support:</strong> Customer Success</li>
            </ul>
          </div>
          
          <div className="bg-blue-50 dark:bg-blue-900 rounded-lg p-6">
            <div className="text-4xl text-blue-500 mb-4 text-center">⚙️</div>
            <h3 className="text-xl font-bold text-blue-700 dark:text-blue-300 mb-4 text-center">Tech Stack</h3>
            <ul className="text-sm text-blue-600 dark:text-blue-400 space-y-2">
              <li>• <strong>Frontend:</strong> React, TypeScript</li>
              <li>• <strong>Backend:</strong> Node.js, PostgreSQL</li>
              <li>• <strong>IoT:</strong> LoRaWAN, MQTT</li>
              <li>• <strong>Cloud:</strong> AWS, Docker</li>
              <li>• <strong>AI/ML:</strong> Python, TensorFlow</li>
            </ul>
          </div>
          
          <div className="bg-green-50 dark:bg-green-900 rounded-lg p-6">
            <div className="text-4xl text-green-500 mb-4 text-center">📅</div>
            <h3 className="text-xl font-bold text-green-700 dark:text-green-300 mb-4 text-center">Timeline</h3>
            <ul className="text-sm text-green-600 dark:text-green-400 space-y-2">
              <li>• <strong>Woche 1-4:</strong> Team Setup</li>
              <li>• <strong>Woche 5-12:</strong> MVP Development</li>
              <li>• <strong>Woche 13-16:</strong> Hardware Tests</li>
              <li>• <strong>Woche 17-20:</strong> Pilotprojekte</li>
              <li>• <strong>Woche 21+:</strong> Go-to-Market</li>
            </ul>
          </div>
        </div>
        
        <div className="mt-6 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 rounded-xl text-center">
          <h3 className="text-2xl font-bold mb-2">🚀 Ready to Execute</h3>
          <p className="text-lg">Erfahrenes Team + bewährte Technologien + klarer Zeitplan = Erfolg garantiert!</p>
        </div>
      </div>,
      
      // Slide 15: Call to Action
      <div className="p-8 h-full bg-gradient-to-br from-blue-600 to-purple-600 text-white">
        <div className="flex flex-col items-center justify-center h-full text-center">
          <div className="text-8xl mb-8">🚀</div>
          <h1 className="text-5xl font-bold mb-6">Bereit für die Messdienst-Revolution?</h1>
          <h2 className="text-2xl font-medium mb-12">Lassen Sie uns gemeinsam Techem & ISTA herausfordern!</h2>
          
          <div className="grid grid-cols-2 gap-8 w-full max-w-4xl mb-12">
            <div className="bg-white bg-opacity-20 rounded-lg p-6 backdrop-blur">
              <h3 className="text-xl font-bold mb-4">📞 Kontakt</h3>
              <div className="space-y-2 text-lg">
                <p>📧 info@clara360.de</p>
                <p>📱 +49 6221 513-0</p>
                <p>🌐 clara360.de/messdienst</p>
              </div>
            </div>
            
            <div className="bg-white bg-opacity-20 rounded-lg p-6 backdrop-blur">
              <h3 className="text-xl font-bold mb-4">🎯 Nächste Schritte</h3>
              <div className="space-y-2 text-lg">
                <p>1️⃣ Kostenlose Beratung</p>
                <p>2️⃣ Pilotprojekt planen</p>
                <p>3️⃣ Einsparungen realisieren</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-green-500 to-yellow-500 text-black p-6 rounded-xl text-center">
            <h3 className="text-3xl font-bold mb-2">💰 Sparen Sie ab sofort 60-80% Ihrer Messdienst-Kosten!</h3>
            <p className="text-xl">Vereinbaren Sie noch heute Ihren kostenlosen Beratungstermin!</p>
          </div>
        </div>
      </div>
    ];
    
    return slides[slideIndex] || (
      <div className="flex flex-col items-center justify-center h-full bg-gradient-to-br from-blue-500 to-purple-600 text-white p-16">
        <div className="text-6xl mb-8">🎯</div>
        <h1 className="text-5xl font-bold mb-8">Slide {slideIndex + 1}</h1>
        <p className="text-2xl text-center">Clara360 Messdienst-Revolution</p>
        <p className="text-lg mt-8 opacity-80">Weitere Killer-Argumente gegen die Konkurrenz</p>
      </div>
    );
  }

  function getSlideTitle(slideIndex) {
    const titles = [
      "Titelseite",
      "Marktchance", 
      "Wettbewerbsanalyse",
      "Kostenstruktur",
      "Etablierte vs. Innovation",
      "Einsparungsrechnung",
      "Mieterfreundlich",
      "Nachhaltigkeit & ESG",
      "Unique Advantage",
      "Killer Features",
      "Revenue Model",
      "Roadmap",
      "Financials",
      "Implementation",
      "Call-to-Action"
    ];
    return titles[slideIndex] || "Slide";
  }
};

export default SuppliersPage;

